import { useState } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useAuth, apiErrorMessage } from "@/lib/auth";

/**
 * Forced or voluntary password change. When the user has must_change_password,
 * the dashboards render this before granting access to anything else.
 */
export function AdminChangePassword({ forced = false, onDone }) {
  const { changePassword, logout, user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 10) return setErr("New password must be at least 10 characters.");
    if (next !== confirm) return setErr("New passwords do not match.");
    setBusy(true);
    try {
      await changePassword(current, next);
      setOk(true);
      if (onDone) setTimeout(onDone, 800);
    } catch (e2) {
      setErr(apiErrorMessage(e2, "Could not change password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-wafa-cream/40" data-testid="change-password-page">
      <div className="mx-auto max-w-md px-4 py-24 lg:px-6">
        <div className="rounded-lg border border-wafa-deep/10 bg-white p-8">
          {forced ? (
            <div className="mb-4 rounded-md border border-wafa-sunshine/40 bg-wafa-sunshine/10 p-3 text-xs text-wafa-deep" data-testid="force-change-banner">
              You must change your temporary password before you can continue.
            </div>
          ) : null}
          <h1 className="font-heading text-2xl font-semibold text-wafa-deep">Change password</h1>
          <p className="mt-2 text-sm text-wafa-ink/70">
            Signed in as <span className="font-mono">{user?.email}</span>.
          </p>

          {ok ? (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-wafa-primary/30 bg-wafa-primary/5 p-3 text-sm text-wafa-deep" data-testid="change-password-success">
              <CheckCircle2 className="h-4 w-4 text-wafa-primary" />
              Password updated.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
              <Field label="Current password" value={current} onChange={setCurrent} type="password" testid="cp-current" />
              <Field label="New password (min 10 chars)" value={next} onChange={setNext} type="password" testid="cp-new" />
              <Field label="Confirm new password" value={confirm} onChange={setConfirm} type="password" testid="cp-confirm" />
              {err ? (
                <div className="flex items-center gap-2 text-sm text-wafa-tomato" data-testid="change-password-error">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              ) : null}
              <button
                disabled={busy}
                data-testid="change-password-submit"
                className="w-full rounded-md bg-wafa-primary py-3 text-sm font-semibold text-white hover:bg-wafa-deep disabled:opacity-60"
              >
                {busy ? "Saving…" : "Change password"}
              </button>
              {forced ? (
                <button
                  type="button"
                  onClick={logout}
                  className="w-full rounded-md border border-wafa-deep/15 py-2 text-xs text-wafa-ink/70 hover:bg-wafa-cream/40"
                  data-testid="change-password-logout"
                >
                  Sign out
                </button>
              ) : null}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-wafa-deep">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 h-11 w-full rounded-md border border-wafa-deep/15 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-wafa-primary"
      />
    </div>
  );
}
